import { useId, useState } from 'react';
import { CircleAlert, CircleCheck, LoaderCircle, Upload } from 'lucide-react';
import { getErrorMessage } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatFileSize, formatNumber } from '@blimo/shared';
import { Button, Checkbox, cn, FileDropzone, Label, toast } from '@blimo/ui';

/**
 * Upload mehrerer Dateien in einen Ordner – nacheinander (der Endpunkt nimmt je Anfrage eine Datei), mit Fortschritt je Datei.
 * Optionen: sofort veröffentlichen (`documents.publish`); bei Liegenschafts-Ordnern zusätzlich Eigentümer benachrichtigen
 * (je Dokument eine E-Mail, F-403) und Datei anhängen. Größe/Typ prüft der Server (max. 64 MB, Inhaltstyp).
 * @param {{ folder: any, ownerType: 'property'|'unit'|'tenancy'|'global', canPublish: boolean, onUploaded: () => unknown }} props
 */
export function DocumentUpload({ folder, ownerType, canPublish, onUploaded }) {
  const api = useApi();
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const [items, setItems] = useState(
    /** @type {Array<{ key: string, file: File, progress: number, status: 'queued'|'uploading'|'done'|'error', error?: string }>} */ ([]),
  );
  const [publish, setPublish] = useState(false);
  const [notify, setNotify] = useState(false);
  const [attach, setAttach] = useState(false);
  const ids = { publish: useId(), notify: useId(), attach: useId() };
  const running = items.some((i) => i.status === 'queued' || i.status === 'uploading');
  const canNotify = canPublish && publish && ownerType === 'property';
  const failed = items.filter((i) => i.status === 'error');

  /** @param {string} key @param {Partial<(typeof items)[number]>} patch */
  const update = (key, patch) => setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  async function start() {
    const batch = files.map((file, i) => ({ key: `${Date.now()}-${i}-${file.name}`, file, progress: 0, status: /** @type {const} */ ('queued') }));
    setItems(batch);
    setFiles([]);
    let done = 0;
    let notified = 0;
    for (const item of batch) {
      update(item.key, { status: 'uploading' });
      try {
        const result = await api.admin.documentFolders.uploadWithProgress(
          folder.id,
          {
            file: item.file,
            publish: canPublish && publish ? true : undefined,
            notify_owners: canNotify && notify ? true : undefined,
            attach: canNotify && notify && attach ? true : undefined,
          },
          { onProgress: (fraction) => update(item.key, { progress: fraction }) },
        );
        notified += Number(result?.meta?.notified ?? 0);
        done += 1;
        update(item.key, { status: 'done', progress: 1 });
      } catch (e) {
        update(item.key, { status: 'error', error: getErrorMessage(e, 'Der Upload ist fehlgeschlagen.') });
      }
    }
    await onUploaded();
    // erfolgreiche Zeilen ausblenden, Fehler stehen lassen
    setItems((prev) => prev.filter((i) => i.status === 'error'));
    if (done > 0) {
      const text = `${formatNumber(done)} ${done === 1 ? 'Dokument' : 'Dokumente'} hochgeladen`;
      toast.success(notify && canNotify ? `${text} · ${formatNumber(notified)} Benachrichtigungen versendet` : text);
    }
    if (done < batch.length) toast.error(`${formatNumber(batch.length - done)} von ${formatNumber(batch.length)} Dateien konnten nicht hochgeladen werden.`);
  }

  return (
    <div className="flex flex-col gap-3">
      <FileDropzone
        label={`Dateien für ${folder.name}`}
        files={files}
        onFilesChange={(next) => {
          setFiles(next);
          if (!running && next.length > 0) setItems((prev) => prev.filter((i) => i.status !== 'error'));
        }}
        disabled={running}
        hint="PDF, Bilder, Office-Dokumente, ZIP, CSV/TXT · max. 64 MB je Datei"
      />
      {files.length > 0 && !running && (
        <div className="flex flex-col gap-2">
          {canPublish && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <span className="flex items-center gap-2">
                <Checkbox id={ids.publish} checked={publish} onChange={(e) => setPublish(e.target.checked)} />
                <Label htmlFor={ids.publish} className="font-normal">
                  Sofort veröffentlichen
                </Label>
              </span>
              {canNotify && (
                <>
                  <span className="flex items-center gap-2">
                    <Checkbox id={ids.notify} checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                    <Label htmlFor={ids.notify} className="font-normal">
                      Eigentümer benachrichtigen
                    </Label>
                  </span>
                  <span className="flex items-center gap-2">
                    <Checkbox id={ids.attach} checked={attach} disabled={!notify} onChange={(e) => setAttach(e.target.checked)} />
                    <Label htmlFor={ids.attach} className={cn('font-normal', !notify && 'text-muted-foreground')}>
                      Datei an E-Mail anhängen
                    </Label>
                  </span>
                </>
              )}
            </div>
          )}
          {canNotify && notify && (
            <p className="text-xs text-muted-foreground">
              Alle aktuellen Eigentümer erhalten je Dokument eine E-Mail (nicht im Vorbereitungsmodus).
              {!folder.visible_in_portal && ' Achtung: Der Ordner ist im Portal ausgeblendet.'}
            </p>
          )}
          <div>
            <Button size="sm" onClick={start}>
              <Upload aria-hidden="true" /> {files.length === 1 ? 'Datei hochladen' : `${formatNumber(files.length)} Dateien hochladen`}
            </Button>
          </div>
        </div>
      )}
      {items.length > 0 && (
        <ul className="flex flex-col gap-1.5" aria-label="Upload-Fortschritt">
          {items.map((item) => (
            <li key={item.key} className="flex flex-col gap-1 rounded-md border bg-card px-2.5 py-1.5 text-sm">
              <span className="flex items-center gap-2">
                {item.status === 'done' ? (
                  <CircleCheck className="size-4 shrink-0 text-success" aria-hidden="true" />
                ) : item.status === 'error' ? (
                  <CircleAlert className="size-4 shrink-0 text-destructive" aria-hidden="true" />
                ) : (
                  <LoaderCircle className={cn('size-4 shrink-0 text-muted-foreground', item.status === 'uploading' && 'animate-spin')} aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1 truncate">{item.file.name}</span>
                <span className="numeric text-xs text-muted-foreground">
                  {item.status === 'queued' ? 'wartet' : item.status === 'error' ? 'fehlgeschlagen' : `${Math.round(item.progress * 100)} %`} ·{' '}
                  {formatFileSize(item.file.size)}
                </span>
              </span>
              {item.status === 'error' ? (
                <span role="alert" className="text-xs text-destructive">
                  {item.error}
                </span>
              ) : (
                <span
                  role="progressbar"
                  aria-label={`Upload ${item.file.name}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(item.progress * 100)}
                  className="h-1 overflow-hidden rounded-full bg-muted"
                >
                  <span
                    className={cn('block h-full rounded-full transition-[width]', item.status === 'done' ? 'bg-success' : 'bg-primary')}
                    style={{ width: `${Math.round(item.progress * 100)}%` }}
                  />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {failed.length > 0 && !running && (
        <div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFiles(failed.map((i) => i.file));
              setItems([]);
            }}
          >
            Fehlgeschlagene erneut auswählen
          </Button>
        </div>
      )}
    </div>
  );
}
